import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  type PcoLinkagePayload,
  useCommittedSubsForCostCode,
  useUpdateChangeOrder,
} from "@/hooks/use-change-orders";

const NEW_VENDOR = "__new_vendor__";

/**
 * Re-points a cost-code-linked PCO at a now-committed subcontractor.
 *
 * Cost-code PCOs author with `newVendor: true` when no sub is committed under
 * the code yet — the server records `pcoRequiresBuyout = true` on the row.
 * That flag is a snapshot from creation, so once the cost code is bought out
 * later the PCO still reads as "Buyout pending" and approval stays blocked.
 *
 * PATCH /change-orders/:id with a fresh `linkage` re-runs resolvePcoLinkage,
 * which clears the flag when the user picks a committed sub. This dialog is
 * the UI for that — used only when re-pointing makes sense (cost_code linkage,
 * draft status, buyout pending).
 */
export function EditPcoLinkageDialog({
  open,
  onOpenChange,
  changeOrderId,
  projectId,
  costCodeId,
  costCodeLabel,
  currentCompanyId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  changeOrderId: string;
  projectId: string;
  costCodeId: string;
  costCodeLabel: string;
  currentCompanyId: string | null;
}) {
  const update = useUpdateChangeOrder();
  const subsQuery = useCommittedSubsForCostCode(projectId, costCodeId);
  const subs = subsQuery.data ?? [];

  const [selected, setSelected] = useState<string>("");

  // Re-seed the picker each time the dialog opens so reopening doesn't show
  // a stale choice from the previous edit.
  useEffect(() => {
    if (!open) return;
    setSelected(currentCompanyId ?? "");
  }, [open, currentCompanyId]);

  const isNewVendor = selected === NEW_VENDOR;
  const canSave = !!selected && !update.isPending;

  function handleSave() {
    const linkage: PcoLinkagePayload = {
      linkageType: "cost_code",
      costCodeId,
      selectedCompanyId: isNewVendor ? null : selected,
      newVendor: isNewVendor,
    };
    update.mutate({ id: changeOrderId, linkage }, { onSuccess: () => onOpenChange(false) });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Re-point PCO to committed sub</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="text-sm text-gray-600">
            Cost code <span className="font-medium text-gray-900">{costCodeLabel}</span> — pick the
            sub now committed under it. This clears the buyout-pending flag so the PCO can be
            approved.
          </div>

          <div className="space-y-1.5">
            <Label>Subcontractor</Label>
            <Select value={selected} onValueChange={setSelected}>
              <SelectTrigger>
                <SelectValue
                  placeholder={subsQuery.isLoading ? "Loading subs…" : "Pick a committed sub"}
                />
              </SelectTrigger>
              <SelectContent>
                {subs.map((s) => (
                  <SelectItem key={s.companyId} value={s.companyId}>
                    {s.companyName} — {s.commitmentNumber}
                  </SelectItem>
                ))}
                {subs.length > 0 && <div className="my-1 border-t border-gray-200" />}
                <SelectItem value={NEW_VENDOR}>+ New Vendor (keep buyout pending)</SelectItem>
              </SelectContent>
            </Select>
            {!subsQuery.isLoading && subs.length === 0 && (
              <p className="text-xs text-amber-700 pt-1">
                No subs are committed under this cost code yet. Award a sub in the Buy Log first,
                then return here.
              </p>
            )}
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={update.isPending}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={!canSave}
            className="bg-[#334155] hover:bg-[#415A77] text-white"
          >
            {update.isPending ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
